// Etiqueta adesiva do rastreador (`/trackers` → "Etiqueta"). Regra PURA
// (camada 3a): tamanhos de etiqueta, tamanho do QR em milímetros, o selo da
// marca no centro do QR e o documento que vai pra impressora. A tela só desenha.
//
// O QR carrega o IMEI cru (15 dígitos), e nada mais: quem lê na entrega dos
// aparelhos quer o IMEI, e um QR numérico curto cabe na versão menor da
// matriz, o que deixa o módulo grande o bastante pra impressora térmica.

/// Tamanhos comuns de rolo de etiqueta térmica. O id fica salvo no navegador
/// de quem imprime, então não renomear.
export interface LabelSize {
  id: string;
  widthMm: number;
  heightMm: number;
}

export const LABEL_SIZES: readonly LabelSize[] = [
  { id: '50x30', widthMm: 50, heightMm: 30 },
  { id: '40x25', widthMm: 40, heightMm: 25 },
  { id: '60x40', widthMm: 60, heightMm: 40 },
  { id: '30x30', widthMm: 30, heightMm: 30 },
];

export const DEFAULT_LABEL_SIZE_ID = '50x30';

/// Chave do `localStorage` com o último tamanho usado.
export const LABEL_SIZE_STORAGE_KEY = 'faromaps.trackerLabelSize';

export function labelSizeById(id: string | null | undefined): LabelSize {
  return (
    LABEL_SIZES.find((s) => s.id === id) ?? LABEL_SIZES.find((s) => s.id === DEFAULT_LABEL_SIZE_ID)!
  );
}

export function labelSizeName(s: LabelSize): string {
  return `${s.widthMm} × ${s.heightMm} mm`;
}

/// Módulos de dados por lado: a matriz é sempre a versão 3 (29×29), porque o
/// IMEI numérico cabe na 1 e a tela pede `minVersion={3}`.
///
/// Toda medida de legibilidade daqui (módulo ≥ 5 pontos a 203 dpi, selo ≤ 0,3
/// dos dados) parte desta constante, e ela só vale porque o conteúdo é o IMEI
/// cru de 15 dígitos (`imeiInput` corta em 15). Pôr uma URL no QR sobe a
/// versão e encolhe o módulo. O teste de `labelQrProps` renderiza a matriz e
/// quebra se ela deixar de ter 29 módulos; mudou o conteúdo, refaça a leitura
/// com o roteiro em `infra/dashboard/tests/scripts/label-qr-readback/`.
export const QR_VERSION = 3;
export const QR_MODULES = 29;

/// Zona livre em volta da matriz, em módulos: o mínimo da norma (ISO/IEC
/// 18004). Vai dentro do próprio QR (`marginSize`), não no respiro do
/// adesivo: a borda do adesivo encosta na caixa, que pode ter cor, e aí o
/// leitor só conta o branco impresso.
export const QR_QUIET_MODULES = 4;

/// Fração dos módulos de DADOS (não do QR com margem) cobertos pelo selo da
/// marca, por lado. Medir contra o QR inteiro faria o selo crescer sobre os
/// dados toda vez que a margem aumenta.
///
/// O que a correção H (30%) precisa cobrir é o RECORTE, não o selo desenhado.
/// O `excavate` do `qrcode.react` arredonda o recorte pra módulo inteiro,
/// sempre pra fora: 0,29 dá um selo de 8,41 módulos e um recorte de 9×9
/// (~10% da área de dados, 0,31 do lado). O recorte anda em degraus de dois
/// módulos: 9×9 com o ratio entre ~0,24 e ~0,31, e 11×11 (~14%) logo acima.
/// Então não há folga contínua até o teto de 0,3 do teste, que mede o
/// desenho. Quem decide é a leitura com jsQR a 203 dpi binarizado (na versão
/// 2, com o selo a 0,27, já não lia): mexeu aqui ou na versão, rode o
/// roteiro em `infra/dashboard/tests/scripts/label-qr-readback/`.
///
/// O mínimo impresso do ícone na guideline (10 mm) não cabe aqui: numa
/// etiqueta de 50×30 o selo tem ~6,5 mm, e um de 10 mm comprometeria a
/// leitura. A leitura do IMEI vence.
export const QR_LOGO_RATIO = 0.29;

/// Respiro entre o QR e a borda do adesivo: só a folga de alinhamento do rolo
/// na impressora. A zona livre de verdade é a `QR_QUIET_MODULES`.
const PADDING_MM = 0.5;

export interface LabelLayout {
  paddingMm: number;
  /// Lado do QR inteiro, já com a zona livre.
  qrMm: number;
  /// Lado de um módulo impresso.
  moduleMm: number;
  logoMm: number;
}

const round = (n: number) => Math.round(n * 100) / 100;

/// A etiqueta é só o QR, centrado e no maior quadrado que cabe no adesivo.
/// Sem IMEI nem nº de série em texto: decisão de produto (21/10/2026), o
/// adesivo é lido pelo QR.
export function labelLayout(size: LabelSize): LabelLayout {
  const qr = Math.min(size.widthMm, size.heightMm) - 2 * PADDING_MM;
  const moduleMm = qr / (QR_MODULES + 2 * QR_QUIET_MODULES);
  return {
    paddingMm: PADDING_MM,
    qrMm: round(qr),
    moduleMm: round(moduleMm),
    logoMm: round(moduleMm * QR_MODULES * QR_LOGO_RATIO),
  };
}

/// O que define a matriz do QR, num lugar só: a tela espalha isto no
/// `<QRCodeSVG>` e o teste renderiza com as mesmas props pra conferir os 29
/// módulos. Tamanho, cores e selo ficam na tela (não mudam a matriz).
export function labelQrProps(imei: string) {
  return {
    value: imei,
    // H (30%): o selo da marca no centro come parte dos módulos.
    level: 'H',
    // Versão mínima 3 (29×29): o IMEI cabe na 1, mas o selo no centro come
    // códigos demais numa matriz pequena. Na 2, com o selo em 27%, o
    // decodificador já falhava; a 3 tem quase o dobro de correção e ainda
    // dá módulo de ~0,78 mm na etiqueta de 50×30.
    minVersion: QR_VERSION,
    // Zona livre da norma dentro do próprio QR (ver QR_QUIET_MODULES).
    marginSize: QR_QUIET_MODULES,
  } as const;
}

/// O símbolo da marca num "botom" branco, como a guideline manda no digital
/// (`docs/brand-identity.md`): recorte circular, tinta preta, área de proteção
/// em volta. Sai como data URI porque a impressão roda num documento à parte
/// (iframe), onde um caminho relativo como `/brand/...` não resolve.
///
/// `symbolSvg` é o arquivo canônico `docs/brand/windmaps_symbol.svg`, inteiro.
export function logoBadgeDataUri(symbolSvg: string): string {
  const inner = symbolSvg
    .replace(/<\?xml[^>]*\?>/g, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/currentColor/g, '#000')
    // O <svg> do símbolo vira um <svg> aninhado, posicionado dentro do botom.
    // O viewBox dele é preservado: nada redesenhado.
    .replace(/<svg\b([^>]*)>/, (_m, attrs: string) => {
      const kept = attrs.replace(/\s(width|height|x|y)="[^"]*"/g, '');
      // Respiro de 10% de cada lado: a área de proteção do símbolo.
      return `<svg${kept} x="10" y="10" width="80" height="80">`;
    })
    .trim();
  const badge =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<circle cx="50" cy="50" r="50" fill="#fff"/>' +
    inner +
    '</svg>';
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(badge)}`;
}

/// Depois de salvar o formulário, a etiqueta abre sozinha? Aparelho novo:
/// sim, o fluxo da entrega é cadastrar e colar o adesivo. IMEI corrigido:
/// sim, com aviso, porque o adesivo já colado na caixa ficou errado. Qualquer
/// outra edição (data, nº de série, modelo) não mexe no QR: não abre.
export function labelAfterSave(
  before: { imei: string } | null,
  saved: { imei: string },
): { imeiChanged: boolean } | null {
  if (before == null) return { imeiChanged: false };
  return before.imei !== saved.imei ? { imeiChanged: true } : null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/// Documento HTML que a impressora recebe: uma página do tamanho exato da
/// etiqueta, sem margem. `labelHtml` é o mesmo nó da prévia (estilos inline em
/// mm), então o que sai impresso é o que a tela mostrou.
export function labelPrintDocument(labelHtml: string, size: LabelSize, title: string): string {
  return [
    '<!doctype html>',
    '<html><head><meta charset="utf-8">',
    `<title>${escapeHtml(title)}</title>`,
    '<style>',
    `@page { size: ${size.widthMm}mm ${size.heightMm}mm; margin: 0; }`,
    'html, body { margin: 0; padding: 0; background: #fff; }',
    // Sem isso o Chrome pode "economizar tinta" e clarear o preto do QR.
    '* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
    '</style></head><body>',
    labelHtml,
    '</body></html>',
  ].join('');
}
